import { DataRow, DataTable } from '@fullstory/proto/fs/api/metrics';
import {
  ComputeMetricResponse,
  ComputeMetricRequest as LegacyComputeMetricRequest,
  QueryCacheOptions,
} from '@fullstory/proto/fs/services/alexa';
import { ComputeMetricRequest, MetricDetail } from '@fullstory/proto/fs/services/dx-objects';
import { AppCacheStatus } from '@fullstory/redux-util/utils';
import { WithID } from '@fullstory/util/uid';

export enum MetricsRequestTypes {
  GetMetricRequest = 'metrics/GET_METRICS_REQUEST',
  GetMetricSuccess = 'metrics/GET_METRICS_SUCCESS',
  GetMetricFailure = 'metrics/GET_METRICS_FAILURE',
  GetMetricWithDepsRequest = 'metrics/GET_METRIC_WITH_DEPS_REQUEST',
  GetMetricWithDepsSuccess = 'metrics/GET_METRIC_WITH_DEPS_SUCCESS',
  GetMetricWithDepsFailure = 'metrics/GET_METRIC_WITH_DEPS_FAILURE',
  GetAllMetricsRequest = 'metrics/GET_ALL_METRICS_REQUEST',
  GetAllMetricsSuccess = 'metrics/GET_ALL_METRICS_SUCCESS',
  GetAllMetricsFailure = 'metrics/GET_ALL_METRICS_FAILURE',
  SetMetricRequest = 'metrics/SET_METRIC_REQUEST',
  SetMetricSuccess = 'metrics/SET_METRIC_SUCCESS',
  SetMetricFailure = 'metrics/SET_METRIC_FAILURE',
  MarkViewedRequest = 'metrics/MARK_VIEWED_REQUEST',
  MarkViewedSuccess = 'metrics/MARK_VIEWED_SUCCESS',
  MarkViewedFailure = 'metrics/MARK_VIEWED_FAILURE',
  StarMetricRequest = 'metrics/STAR_METRIC_REQUEST',
  StarMetricSuccess = 'metrics/STAR_METRIC_SUCCESS',
  StarMetricFailure = 'metrics/STAR_METRIC_FAILURE',
  ArchiveMetricRequest = 'metrics/ARCHIVE_METRIC_REQUEST',
  ArchiveMetricSuccess = 'metrics/ARCHIVE_METRIC_SUCCESS',
  ArchiveMetricFailure = 'metrics/ARCHIVE_METRIC_FAILURE',
  CreateUnnamedMetricRequest = 'metrics/CREATE_UNNAMED_METRIC_REQUEST',
  CreateUnnamedMetricSuccess = 'metrics/CREATE_UNNAMED_METRIC_SUCCESS',
  CreateUnnamedMetricFailure = 'metrics/CREATE_UNNAMED_METRIC_FAILURE',
  GetUnnamedMetricRequest = 'metrics/GET_UNNAMED_METRIC_REQUEST',
  GetUnnamedMetricSuccess = 'metrics/GET_UNNAMED_METRIC_SUCCESS',
  GetUnnamedMetricFailure = 'metrics/GET_UNNAMED_METRIC_FAILURE',
  ComputeMetricRequest = 'metrics/COMPUTE_METRIC_REQUEST',
  ComputeMetricSuccess = 'metrics/COMPUTE_METRIC_SUCCESS',
  ComputeMetricFailure = 'metrics/COMPUTE_METRIC_FAILURE',
  LegacyComputeMetricRequest = 'metrics/LEGACY_COMPUTE_METRIC_REQUEST',
  LegacyComputeMetricSuccess = 'metrics/LEGACY_COMPUTE_METRIC_SUCCESS',
  LegacyComputeMetricFailure = 'metrics/LEGACY_COMPUTE_METRIC_FAILURE',
  MetricsAccessRequest = 'metrics/GET_ACCESS_REQUEST',
  MetricsAccessSuccess = 'metrics/GET_ACCESS_SUCCESS',
  MetricsAccessFailure = 'metrics/GET_ACCESS_FAILURE',
  MetricsAppCacheIsLoading = 'metrics/APP_CACHE_IS_LOADING',
  MetricsAppCacheLoadFailed = 'metrics/APP_CACHE_LOAD_FAILED',
  MetricsAppCacheRefetching = 'metrics/APP_CACHE_IS_REFETCHING',
  MetricsAppCacheHasData = 'metrics/APP_CACHE_HAS_DATA',
  MetricsAppCacheFresh = 'metrics/APP_CACHE_FRESH',
  MetricsAppCacheSwap = 'metrics/APP_CACHE_SWAP',
  MetricsAppCacheClear = 'metrics/APP_CACHE_CLEAR',
}

export type MetricsById = { [id: string]: MetricDetail };
export type MetricsState = MetricsById;

export interface LegacyMetricsRequestBody
  extends Omit<LegacyComputeMetricRequest, 'now' | 'timezone' | 'cacheOptions'> {
  cacheOptions?: QueryCacheOptions;
}

export interface MetricsRequestBody extends Omit<ComputeMetricRequest, 'timezone'> {}
export interface ResponseStatus {
  loading: boolean;
  noData: boolean;
  error: boolean;
}

export interface MetricsCacheData {
  status: AppCacheStatus;
  ready: boolean;
  data: ComputeMetricResponseWithId[] | null;
  storageId: string;
  safeMessage: string | null;
}

export type DataRowWithId = WithID<DataRow>;

export interface DataTableWithId extends DataTable {
  table: DataRowWithId[];
}

export interface ComputeMetricResponseWithId extends ComputeMetricResponse {
  data: DataTableWithId | null;
  // will always be false for pre-metriccomputer code, but will be accurate for post-metriccomputer
  isPast: boolean;
  // will be null for pre-metriccomputer code, but will be accurate for post-metriccomputer
  cacheOptions: QueryCacheOptions | null;
}
